import { cookie } from '@playcastdotio/Cookie';
import { createPlayerPeerId, createPlayerPersistentId } from '@playcastdotio/CreateId';
import { PlaycastPeerClient, PlaycastClientPeerStatus, createClient, createQualityManager } from '@playcastdotio/Peer';
import { authStateStore, hostConnectionState, invitationState2, joinIdState, joinState, mediaStreamState, playerNameState } from './stores';
import { handleHostMessage } from './hostMessaging/handleMessages';
import { generators, PeerIdType, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import { sendMetricPlayerBatchWebrtcStats, sendMetricPlayerFrameratePanic, WebrtcQualityStatsMetric } from '../metrics';
import { isAppleDevice, isMobileDevice } from './utility';
import * as realtime from './realtimeMessaging';
import { reject } from 'lodash';

setTimeout(() => {
  realtime.connect();
}, 1000);

export const getPersistentPlayerId = () => {
  const { get, set } = cookie();
  const id = get('playcast-player-id') || localStorage.getItem('playcast-player-id');
  if (id && !id?.startsWith('player_')) {
    // remake the id
    const newId = createPlayerPersistentId();
    set('playcast-player-id', newId);
    localStorage.setItem('playcast-player-id', newId);
    return newId;
  }
  if (id) return id;
  const searchParams = new URLSearchParams(window.location.search);
  const playerId = searchParams.get('id');
  if (playerId) {
    set('playcast-player-id', playerId);
    localStorage.setItem('playcast-player-id', playerId);
    return playerId;
  } else {
    const newId = createPlayerPersistentId();
    set('playcast-player-id', newId);
    localStorage.setItem('playcast-player-id', newId);
    return newId;
  }
};

export const saveName = () => {
  const name = playerNameState.currentState.name;
  localStorage.setItem('name', name);
};

export const startJoin = (joinId?: string) => {
  if (!joinId) {
    console.error('No join id');
    return;
  }
  console.log('Joining with id:', joinId);
  joinIdState.updateState(joinId);

  joinState.updateState({
    state: 'resolvingAlias',
  });
};

let peer: PlaycastPeerClient;

// let reconnecting = false;

export const hardResetDev = () => {
  // reconnecting = true;
  // createPeer(joinIdState.currentState);
  const panicMessage = generators.peer.frameratePanic('player', peer.peerId, true);
  peer.sendMessage(panicMessage);
};

export const qualityManager = createQualityManager();

const PANIC_COUNT_THRESHOLD = 3;
const PANIC_HARD_RESET_TIMEOUT = 4000;
let frameratePanicCount = 0;
let frameratePanicTimeout: NodeJS.Timeout | undefined;
qualityManager.onFrameratePanic((reason) => {
  console.error('Framerate Panic!');
  if (peer) {
    // check we actually have tracks
    let hasTracks = false;
    for (const trackId in peer.incomingTracks.currentState) {
      const track = peer.incomingTracks.currentState[trackId];
      if (track.metadata.type === 'video') {
        hasTracks = true;
        break;
      }
    }

    if (!hasTracks) {
      frameratePanicCount = 0;
      return;
    }

    frameratePanicCount++;
    const hardReset = frameratePanicCount > PANIC_COUNT_THRESHOLD;
    const panicMessage = generators.peer.frameratePanic('player', peer.peerId, hardReset);
    peer.sendMessage(panicMessage);
    sendMetricPlayerFrameratePanic(hardReset, reason);
    // reset the timeout
    if (frameratePanicTimeout) {
      clearTimeout(frameratePanicTimeout);
    }
    frameratePanicTimeout = setTimeout(() => {
      frameratePanicCount = 0;
    }, PANIC_HARD_RESET_TIMEOUT);
  }
});

qualityManager.preferredQualityState.addListener((state) => {
  if (peer) {
    console.log('Changing quality to', state.profile);
    peer.changeQualityProfile(state.profile);
  }
});

export const createPeer = async (hostBroadcastId: string) => {
  if (!hostBroadcastId) {
    console.error('No host broadcast id');
    return;
  }
  if (peer) {
    if (peer.status.currentState.connectionState === 'signalling') {
      console.warn('Peer is already connecting');
      return;
    }
    console.error('Closing existing peer');
    // clear the listeners so we dont get the status update
    peer.status.removeListener();
    peer.close();
  }
  hostConnectionState.updateState({
    ...hostConnectionState.currentState,
    status: 'connecting',
    playerPeerId: '',
  });
  const playerId = await new Promise<string>((resolve) => {
    realtime.sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
        peerIdType: PeerIdType.PLAYER,
      }),
    );
    const timeout = setTimeout(() => {
      reject('Timeout');
    }, 5000);
    const listener = hostConnectionState.addListener((state) => {
      if (state.playerPeerId !== '') {
        clearTimeout(timeout);
        hostConnectionState.removeListener(listener);
        resolve(state.playerPeerId);
      }
    });
  });

  // Get ICE servers from the state
  const iceServers = hostConnectionState.currentState.iceServers;

  peer = createClient(
    {
      realtimeApiManager: {
        send: (message) => {
          realtime.sendMessage(message);
        },
      },
      additionalMetadata: {
        displayName: playerNameState.currentState.name,
        playerPersistentId: getPersistentPlayerId(),
        email: authStateStore.currentState.email,
        playerAuthId: authStateStore.currentState.playerAuthId,
        invitationPlayerId: invitationState2.currentState.invitationPlayerId,
        isAppleDevice: isAppleDevice(),
        isMobileDevice: isMobileDevice(),
      },
      iceServers: iceServers || undefined,
      forceTurn: !hostConnectionState.currentState.allowPeerConnections,
    },
    playerId,
  );
  peer.connect(hostBroadcastId);

  try {
    (window as any).playcast = (window as any).playcast || {};
    (window as any).playcast.peer = peer;
    (window as any).playcast.qualityManager = qualityManager;
  } catch (e) {
    console.error('Could not set window.playcast', e);
  }

  let statsCollectionArray: WebrtcQualityStatsMetric[] = [];

  const checkStatus = (status: PlaycastClientPeerStatus) => {
    console.warn('Status:', status);
    if (status.connectionState === 'closed' || status.connectionState === 'refused') {
      const wasRefused = status.connectionState === 'refused';
      hostConnectionState.updateState({
        ...hostConnectionState.currentState,
        status: wasRefused ? 'refused' : 'disconnected',
      });
      if (statsCollectionArray.length > 0) {
        sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
        realtime.sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
        statsCollectionArray = [];
      }
    }
    if (status.connectionState === 'connected') {
      hostConnectionState.updateState({
        ...hostConnectionState.currentState,
        status: 'connected',
      });
    }
  };
  peer.status.addListener((status) => {
    checkStatus(status);
  });
  checkStatus(peer.status.currentState);
  let debounceTimeout: NodeJS.Timeout | null = null;
  peer.incomingTracks.addListener((tracks) => {
    // Return early if we already have a pending update
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    debounceTimeout = setTimeout(() => {
      console.log('Incoming tracks (debounced):', tracks);
      const allTracks = Object.values(tracks).map((t) => t.track);
      const videoTracks = allTracks.filter((t) => t.kind === 'video');
      const audioTracks = allTracks.filter((t) => t.kind === 'audio');
      const newMediaStream = new MediaStream([...videoTracks, ...audioTracks]);
      mediaStreamState.updateState({
        stream: newMediaStream,
        audioStream: new MediaStream(audioTracks),
      });
      debounceTimeout = null;
    }, 500);
  });
  peer.onMessage((msg) => {
    handleHostMessage(msg);
  });
  setInterval(() => {
    // log info about the audio track.
    const stream = mediaStreamState.currentState.audioStream;
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('Audio track:', audioTrack);
      }
    }
  }, 3000);

  peer.statistics.addListener((stats) => {
    console.log('Quality stats:', stats.quality);
    qualityManager.parseQualityStats(stats.quality);
    statsCollectionArray.push({
      recommendation: stats.quality.recommendation,
      reason: stats.quality.reason,
      rawStats: stats.quality.rawStats,
    });
    if (statsCollectionArray.length >= 10) {
      sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
      realtime.sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
      statsCollectionArray = [];
    }
  });

  return peer;
};

export const playerDisconnect = () => {
  joinState.updateState({
    ...joinState.currentState,
    state: 'playerDisconnected',
  });
};

export const getPeer = (): PlaycastPeerClient | undefined => {
  return peer;
};

// export const changeProfile = (profile: PlaycastDefaultProfiles) => {
//   if (peer) {
//     console.log('Changing profile to', profile);
//     peer.changeRequestedProfile(profile);
//   }
// };
